import React, { useState } from 'react';
import { C } from '../../shared/design-tokens.js';
import Drawer from '../../shared/ui/Drawer.js';
import { CONTROL_INPUT, MUTED_LABEL } from './scheduler-ui.js';
import { apiWrite } from '../../lib/api-client.js';
import type { SchedulerDriver, TerminalOption, DriverConstraint, TimeOffRequest } from './use-scheduler-read-model.js';
import { useSchedulerWrite } from './use-scheduler-write.js';

interface EndAssignmentButtonProps {
  driver: SchedulerDriver;
  onAssignmentChange?: () => void;
}

function EndAssignmentButton({ driver, onAssignmentChange }: EndAssignmentButtonProps) {
  const [confirming, setConfirming] = useState(false);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [endNotes, setEndNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  async function handleEnd() {
    setBusy(true);
    setActionError(null);
    setResult(null);

    const { ok, data } = await apiWrite<{ error?: string }>('/api/scheduler/unassign', 'POST', {
      employee_id: driver.id,
      end_date: endDate,
      notes: endNotes || null,
    });
    setBusy(false);

    if (!ok) {
      setActionError(data?.error || 'Failed to end assignment');
      return;
    }

    setResult('Assignment ended');
    setConfirming(false);
    setEndNotes('');
    if (onAssignmentChange) onAssignmentChange();
  }

  if (result) {
    return <div style={{ color: C.green, fontSize: 12, marginBottom: 12 }}>{result}</div>;
  }

  if (!confirming) {
    return (
      <button
        type="button"
        onClick={() => setConfirming(true)}
        style={{
          padding: '6px 14px', borderRadius: 6, border: `1px solid ${C.red}`, cursor: 'pointer',
          background: 'transparent', color: C.red, fontSize: 12, fontWeight: 600, marginBottom: 12,
        }}
      >
        End assignment
      </button>
    );
  }

  return (
    <div style={{ padding: 12, borderRadius: 10, background: C.panel, border: `1px solid ${C.red}`, marginBottom: 12 }}>
      <div style={{ color: C.red, fontSize: 13, marginBottom: 10 }}>End current assignment</div>

      <label style={MUTED_LABEL}>End date</label>
      <input
        type="date"
        value={endDate}
        onChange={(e) => setEndDate(e.target.value)}
        style={{ ...CONTROL_INPUT, marginBottom: 10 }}
        required
      />

      <label style={MUTED_LABEL}>Notes (optional)</label>
      <input
        type="text"
        value={endNotes}
        onChange={(e) => setEndNotes(e.target.value)}
        placeholder="Reason for ending assignment..."
        style={{ ...CONTROL_INPUT, marginBottom: 12 }}
      />

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={handleEnd}
          disabled={busy}
          style={{
            padding: '6px 14px', borderRadius: 6, border: 'none', cursor: busy ? 'not-allowed' : 'pointer',
            background: C.red, color: '#fff', fontSize: 12, fontWeight: 600, opacity: busy ? 0.6 : 1,
          }}
        >
          {busy ? 'Ending...' : 'Confirm end'}
        </button>
        <button
          type="button"
          onClick={() => { setConfirming(false); setActionError(null); }}
          style={{
            padding: '6px 14px', borderRadius: 6, border: `1px solid ${C.borderSoft}`, cursor: 'pointer',
            background: 'transparent', color: C.textMuted, fontSize: 12,
          }}
        >
          Cancel
        </button>
      </div>

      {actionError ? <div style={{ color: C.red, fontSize: 11, marginTop: 6 }}>{actionError}</div> : null}
    </div>
  );
}

interface AssignmentFormProps {
  driver: SchedulerDriver;
  terminalOptions: TerminalOption[];
  onAssignmentChange?: () => void;
}

function AssignmentForm({ driver, terminalOptions, onAssignmentChange }: AssignmentFormProps) {
  const current = driver.current_assignment || null;
  const realTerminals = (terminalOptions || []).filter((t: TerminalOption) => t.id !== 'all');

  const [assignmentType, setAssignmentType] = useState(current?.assignment_type || 'terminal');
  const [terminalId, setTerminalId] = useState(current?.terminal_id ? String(current.terminal_id) : (realTerminals[0] ? String(realTerminals[0].id) : ''));
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setActionError(null);
    setResult(null);

    const payload: {
      employee_id: string | number;
      assignment_type: string;
      start_date: string;
      notes: string | null;
      terminal_id?: string;
    } = {
      employee_id: driver.id,
      assignment_type: assignmentType,
      start_date: startDate,
      notes: notes || null,
    };

    if (assignmentType === 'terminal') {
      payload.terminal_id = terminalId;
    }

    const { ok, data } = await apiWrite<{ error?: string; event_type?: string }>('/api/scheduler/assign', 'POST', payload);
    setBusy(false);

    if (!ok) {
      setActionError(data?.error || 'Assignment failed');
      return;
    }

    setResult(data.event_type === 'transfer_created' ? 'Transferred' : data.event_type === 'sent_home' ? 'Sent home' : 'Assigned');
    setNotes('');
    if (onAssignmentChange) onAssignmentChange();
  }

  const isReassign = !!current;

  return (
    <form onSubmit={handleSubmit} style={{ padding: 12, borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}`, marginBottom: 12 }}>
      <div style={{ color: C.gold, fontSize: 13, marginBottom: 10 }}>{isReassign ? 'Reassign driver' : 'Create assignment'}</div>

      <label style={MUTED_LABEL}>Assignment type</label>
      <select
        value={assignmentType}
        onChange={(e) => setAssignmentType(e.target.value)}
        style={{ ...CONTROL_INPUT, marginBottom: 10 }}
      >
        <option value="terminal">Terminal</option>
        <option value="home">Home</option>
        <option value="off_rotation">Off rotation</option>
      </select>

      {assignmentType === 'terminal' ? (
        <>
          <label style={MUTED_LABEL}>Terminal</label>
          <select
            value={terminalId}
            onChange={(e) => setTerminalId(e.target.value)}
            style={{ ...CONTROL_INPUT, marginBottom: 10 }}
          >
            {realTerminals.map((t: TerminalOption) => (
              <option key={t.id} value={String(t.id)}>{t.name}</option>
            ))}
          </select>
        </>
      ) : null}

      <label style={MUTED_LABEL}>Start date</label>
      <input
        type="date"
        value={startDate}
        onChange={(e) => setStartDate(e.target.value)}
        style={{ ...CONTROL_INPUT, marginBottom: 10 }}
        required
      />

      <label style={MUTED_LABEL}>Notes (optional)</label>
      <input
        type="text"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Reason for assignment..."
        style={{ ...CONTROL_INPUT, marginBottom: 12 }}
      />

      <button
        type="submit"
        disabled={busy || (assignmentType === 'terminal' && !terminalId)}
        style={{
          padding: '8px 18px', borderRadius: 6, border: 'none', cursor: busy ? 'not-allowed' : 'pointer',
          background: C.gold, color: '#000', fontSize: 13, fontWeight: 600, opacity: busy ? 0.6 : 1,
        }}
      >
        {busy ? 'Saving...' : isReassign ? 'Reassign' : 'Assign'}
      </button>

      {actionError ? <div style={{ color: C.red, fontSize: 11, marginTop: 6 }}>{actionError}</div> : null}
      {result ? <div style={{ color: C.green, fontSize: 11, marginTop: 6 }}>{result} successfully</div> : null}
    </form>
  );
}

const CONSTRAINT_TYPES = [
  { value: 'no_day', label: 'No specific day' },
  { value: 'no_weekends', label: 'No weekends' },
  { value: 'every_other_weekend', label: 'Every other weekend' },
  { value: 'max_days_per_week', label: 'Max days per week' },
  { value: 'custom', label: 'Custom' },
];

const TIME_OFF_TYPES = [
  { value: 'sick', label: 'Sick' },
  { value: 'personal', label: 'Personal' },
  { value: 'pto', label: 'PTO' },
  { value: 'rotation_off', label: 'Rotation Off' },
  { value: 'other', label: 'Other' },
];

interface SchedulerDriverDetailDrawerProps {
  driver: SchedulerDriver | null;
  onClose: () => void;
  terminalOptions: TerminalOption[];
  onAssignmentChange?: () => void;
}

export default function SchedulerDriverDetailDrawer({ driver, onClose, terminalOptions, onAssignmentChange }: SchedulerDriverDetailDrawerProps) {
  const [showAddConstraint, setShowAddConstraint] = useState(false);
  const [constraintType, setConstraintType] = useState('custom');
  const [constraintDay, setConstraintDay] = useState('');
  const [constraintDesc, setConstraintDesc] = useState('');
  const [showTimeOffForm, setShowTimeOffForm] = useState(false);
  const [toType, setToType] = useState('sick');
  const [toStart, setToStart] = useState('');
  const [toEnd, setToEnd] = useState('');
  const [toReason, setToReason] = useState('');

  const {
    addConstraint,
    deleteConstraint,
    requestTimeOff,
    submitting: writeSubmitting,
    error: writeError,
    clearError,
  } = useSchedulerWrite(() => {
    if (onAssignmentChange) onAssignmentChange();
  });

  if (!driver) return null;

  const assignment = driver.current_assignment || null;
  const timeOffRequests = (driver.time_off_requests || []).slice(0, 5);
  const constraints = driver.constraints || [];
  const balance = driver.pto_balance || null;
  const remaining = balance ? (balance.annual_days || 0) - (balance.used_days || 0) : null;
  const today = new Date().toISOString().split('T')[0];

  async function handleAddConstraint() {
    if (!driver) return;
    if (!constraintDesc.trim()) return;
    const success = await addConstraint(
      driver.id,
      constraintType,
      constraintDesc.trim(),
      constraintDay ? parseInt(constraintDay, 10) : null,
    );
    if (success) {
      setShowAddConstraint(false);
      setConstraintType('custom');
      setConstraintDay('');
      setConstraintDesc('');
    }
  }

  async function handleRequestTimeOff() {
    if (!driver) return;
    if (!toStart || !toEnd) return;
    const success = await requestTimeOff(driver.id, toType, toStart, toEnd, toReason || undefined);
    if (success) {
      setShowTimeOffForm(false);
      setToType('sick');
      setToStart('');
      setToEnd('');
      setToReason('');
    }
  }

  const drawerTitle =
    [driver.first_name, driver.last_name].filter(Boolean).join(' ').trim() ||
    (driver.driver_id ? `Driver ${driver.driver_id}` : 'Driver');

  return (
    <Drawer open={true} onClose={onClose} title={drawerTitle} width={400}>

      <div style={{ color: C.text, fontSize: 20, fontWeight: 700, marginBottom: 6 }}>{driver.first_name} {driver.last_name}</div>
      <div style={{ color: C.textMuted, fontSize: 12, marginBottom: 4 }}>{driver.driver_id || 'No driver ID'} · {(driver.staffing_type || 'local').replace(/_/g, ' ')}</div>
      <div style={{ color: C.textMuted, fontSize: 12, marginBottom: 16 }}>{(driver.role || 'driver').replace(/_/g, ' ')}</div>

      <div style={{ padding: 12, borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}`, marginBottom: 12 }}>
        <div style={{ color: C.gold, fontSize: 13, marginBottom: 6 }}>Current assignment</div>
        <div style={{ color: C.textMuted, fontSize: 12 }}>{assignment ? (assignment.terminal_name || assignment.assignment_type || 'Assigned') : 'Unassigned'}</div>
        {assignment?.started_at ? <div style={{ color: C.textMuted, fontSize: 11, marginTop: 2 }}>Since {assignment.started_at}</div> : null}
        {assignment?.notes ? <div style={{ color: C.textMuted, fontSize: 11, marginTop: 2 }}>{assignment.notes}</div> : null}
      </div>

      {assignment ? <EndAssignmentButton driver={driver} onAssignmentChange={onAssignmentChange} /> : null}

      <AssignmentForm driver={driver} terminalOptions={terminalOptions} onAssignmentChange={onAssignmentChange} />

      {/* Constraints section with add/delete */}
      <div style={{ padding: 12, borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}`, marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <div style={{ color: C.gold, fontSize: 13 }}>Constraints</div>
          <button
            type="button"
            onClick={() => { setShowAddConstraint(!showAddConstraint); clearError(); }}
            style={{ background: 'none', border: 'none', color: C.gold, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
          >
            {showAddConstraint ? 'Cancel' : '+ Add'}
          </button>
        </div>
        {constraints.length ? constraints.map((constraint: DriverConstraint) => (
          <div key={constraint.id || constraint.description} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <div style={{ color: C.textMuted, fontSize: 12 }}>{constraint.description || constraint.constraint_type}</div>
            {constraint.id && (
              <button
                type="button"
                onClick={() => deleteConstraint(constraint.id!)}
                disabled={writeSubmitting}
                style={{ background: 'none', border: 'none', color: C.red, fontSize: 11, cursor: 'pointer', opacity: writeSubmitting ? 0.5 : 1 }}
              >
                ×
              </button>
            )}
          </div>
        )) : <div style={{ color: C.textMuted, fontSize: 12 }}>No constraints</div>}

        {showAddConstraint && (
          <div style={{ marginTop: 8, padding: 10, borderRadius: 8, background: 'rgba(255,255,255,0.02)', border: `1px solid ${C.borderSoft}` }}>
            <label style={MUTED_LABEL}>Type</label>
            <select value={constraintType} onChange={(e) => setConstraintType(e.target.value)} style={{ ...CONTROL_INPUT, marginBottom: 8 }}>
              {CONSTRAINT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            {constraintType === 'no_day' && (
              <>
                <label style={MUTED_LABEL}>Day of week (0=Sun, 6=Sat)</label>
                <input type="number" min="0" max="6" value={constraintDay} onChange={(e) => setConstraintDay(e.target.value)} style={{ ...CONTROL_INPUT, marginBottom: 8 }} />
              </>
            )}
            <label style={MUTED_LABEL}>Description</label>
            <input type="text" value={constraintDesc} onChange={(e) => setConstraintDesc(e.target.value)} placeholder="e.g. No Sundays" style={{ ...CONTROL_INPUT, marginBottom: 8 }} />
            <button
              type="button"
              onClick={handleAddConstraint}
              disabled={!constraintDesc.trim() || writeSubmitting}
              style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: C.gold, color: '#000', fontSize: 12, fontWeight: 600, cursor: !constraintDesc.trim() || writeSubmitting ? 'not-allowed' : 'pointer', opacity: !constraintDesc.trim() || writeSubmitting ? 0.5 : 1 }}
            >
              {writeSubmitting ? 'Adding...' : 'Add constraint'}
            </button>
          </div>
        )}
      </div>

      {/* Time off section with request form */}
      <div style={{ padding: 12, borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}`, marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <div style={{ color: C.gold, fontSize: 13 }}>Time off snapshot</div>
          <button
            type="button"
            onClick={() => { setShowTimeOffForm(!showTimeOffForm); clearError(); }}
            style={{ background: 'none', border: 'none', color: C.gold, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
          >
            {showTimeOffForm ? 'Cancel' : '+ Request'}
          </button>
        </div>
        {timeOffRequests.length ? timeOffRequests.map((request: TimeOffRequest) => (
          <div key={request.id} style={{ color: C.textMuted, fontSize: 12, marginBottom: 4 }}>
            {String(request.status || 'pending').toUpperCase()} · {request.request_type} · {request.start_date} → {request.end_date}
          </div>
        )) : <div style={{ color: C.textMuted, fontSize: 12 }}>No requests</div>}

        {showTimeOffForm && (
          <div style={{ marginTop: 8, padding: 10, borderRadius: 8, background: 'rgba(255,255,255,0.02)', border: `1px solid ${C.borderSoft}` }}>
            <label style={MUTED_LABEL}>Type</label>
            <select value={toType} onChange={(e) => setToType(e.target.value)} style={{ ...CONTROL_INPUT, marginBottom: 8 }}>
              {TIME_OFF_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <div style={{ flex: 1 }}>
                <label style={MUTED_LABEL}>Start</label>
                <input type="date" value={toStart} min={today} onChange={(e) => { setToStart(e.target.value); if (!toEnd || e.target.value > toEnd) setToEnd(e.target.value); }} style={CONTROL_INPUT} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={MUTED_LABEL}>End</label>
                <input type="date" value={toEnd} min={toStart || today} onChange={(e) => setToEnd(e.target.value)} style={CONTROL_INPUT} />
              </div>
            </div>
            <label style={MUTED_LABEL}>Reason (optional)</label>
            <input type="text" value={toReason} onChange={(e) => setToReason(e.target.value)} placeholder="Reason..." style={{ ...CONTROL_INPUT, marginBottom: 8 }} />
            <button
              type="button"
              onClick={handleRequestTimeOff}
              disabled={!toStart || !toEnd || writeSubmitting}
              style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: C.gold, color: '#000', fontSize: 12, fontWeight: 600, cursor: !toStart || !toEnd || writeSubmitting ? 'not-allowed' : 'pointer', opacity: !toStart || !toEnd || writeSubmitting ? 0.5 : 1 }}
            >
              {writeSubmitting ? 'Submitting...' : 'Submit request'}
            </button>
          </div>
        )}
      </div>

      {writeError && <div style={{ color: C.red, fontSize: 11, marginBottom: 8, padding: 8, borderRadius: 6, background: 'rgba(255,107,107,0.1)' }}>{writeError}</div>}

      <div style={{ padding: 12, borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}` }}>
        <div style={{ color: C.gold, fontSize: 13, marginBottom: 6 }}>PTO</div>
        {driver.pto_eligible && balance ? (
          <>
            <div style={{ color: C.textMuted, fontSize: 12 }}>Annual: {balance.annual_days || 0}</div>
            <div style={{ color: C.textMuted, fontSize: 12 }}>Used: {balance.used_days || 0}</div>
            <div style={{ color: (remaining ?? 0) > 0 ? C.gold : C.red, fontSize: 12, marginTop: 4 }}>Remaining: {remaining}</div>
          </>
        ) : (
          <div style={{ color: C.textMuted, fontSize: 12 }}>Not PTO eligible</div>
        )}
      </div>
    </Drawer>
  );
}
